import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  CheckCircleOutline,
  CloudUploadOutlined,
  ContentCopy,
  DownloadOutlined,
  ErrorOutline,
  SyncProblemOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { buildDownloadUrl, fetchData } from '../../../helpers/apiUtils';
import {
  CloudConfig,
  CloudEntry,
  CloudExcluded,
  CloudPreview,
  CloudReportRow,
  CloudSiteStatus,
  CloudStatus,
  EXCLUSION_LABEL,
} from './types';

/**
 * Reiter „Broker-Cloud" — Footer-Logo-Leiste aller sechs Portale (Plan 2026-10-08).
 *
 * Die Liste kommt aus dem Hub (ausgespielte Profile) plus diesen Einstellungen. „Speichern"
 * veroeffentlicht sofort; der 10-Minuten-Lauf des Servers gleicht ausserdem jede Aenderung an
 * Profilen und Katalog von selbst nach. Die Vorschau rechnet die UNGESPEICHERTE Einstellung und
 * zaehlt nie (der Server baut sie ohne Zaehl-Endpunkt).
 */

const API = '/api/broker-hub/cloud';

function vorschauHtml(siteKey: string, code: string): string {
  const sicher = code.replace(/<\/script/gi, '<\\/script');
  // Der zweite Skriptblock meldet die Inhaltshoehe an das Dashboard (sandbox ohne same-origin:
  // nur postMessage kommt heraus), damit das iframe keine Logo-Reihe abschneidet.
  const hoehe =
    "new ResizeObserver(function(){parent.postMessage({bhcHoehe:document.documentElement.scrollHeight},'*')}).observe(document.body);";
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;background:#fff;font-family:system-ui,sans-serif}</style></head><body><div id="bhc-${siteKey}"></div><script>${sicher}</script><script>${hoehe}</script></body></html>`;
}

function heuteBerlin(offsetTage = 0): string {
  const d = new Date(Date.now() - offsetTage * 86_400_000);
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
}

const ZAHL_FELDER: Array<{
  key: keyof CloudConfig['style'];
  label: string;
  min: number;
  max: number;
}> = [
  { key: 'logoHeight', label: 'Logohöhe (px)', min: 20, max: 90 },
  { key: 'gap', label: 'Abstand Logos (px)', min: 0, max: 48 },
  { key: 'maxWidth', label: 'Max. Breite (px)', min: 320, max: 2000 },
  { key: 'marginTop', label: 'Abstand oben (px)', min: 0, max: 120 },
  { key: 'marginBottom', label: 'Abstand unten (px)', min: 0, max: 120 },
];
const FARB_FELDER: Array<{ key: keyof CloudConfig['style']; label: string }> = [
  { key: 'linkColor', label: 'Farbe Vergleichs-Link' },
  { key: 'noteColor', label: 'Farbe „Anzeige"' },
  { key: 'background', label: 'Hintergrund' },
];

function StatusChip({ s }: { s: CloudSiteStatus }) {
  if (s.last_error) {
    return (
      <Tooltip title={`${s.last_error.at}: ${s.last_error.message}`}>
        <Chip size="small" color="error" icon={<ErrorOutline />} label="Fehler" />
      </Tooltip>
    );
  }
  if (!s.published) return <Chip size="small" variant="outlined" label="nie veröffentlicht" />;
  if (!s.in_sync) {
    return (
      <Tooltip title="Die berechnete Leiste weicht von der veröffentlichten ab — der nächste Lauf (≤ 10 min) gleicht sie an, oder jetzt veröffentlichen.">
        <Chip size="small" color="warning" icon={<SyncProblemOutlined />} label="weicht ab" />
      </Tooltip>
    );
  }
  return <Chip size="small" color="success" icon={<CheckCircleOutline />} label="aktuell" />;
}

const BrokerCloudTab: React.FC = () => {
  const [status, setStatus] = useState<CloudStatus | null>(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [siteKey, setSiteKey] = useState<string>('ft');
  const [form, setForm] = useState<CloudConfig | null>(null);
  const [preview, setPreview] = useState<CloudPreview | null>(null);
  const [mobil, setMobil] = useState(false);
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);
  const formSiteRef = useRef<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeHoehe, setIframeHoehe] = useState(200);

  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (!iframeRef.current || ev.source !== iframeRef.current.contentWindow) return;
      const h = Number((ev.data as { bhcHoehe?: unknown })?.bhcHoehe);
      if (Number.isFinite(h)) setIframeHoehe(Math.min(900, Math.max(80, Math.ceil(h))));
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const ladeStatus = useCallback(async () => {
    setLaden(true);
    const r = await fetchData<CloudStatus>(API);
    if (r.success && r.data) {
      setStatus(r.data);
      setFehler(null);
    } else {
      setFehler(r.error ?? 'Laden fehlgeschlagen');
    }
    setLaden(false);
  }, []);

  useEffect(() => {
    ladeStatus();
  }, [ladeStatus]);

  const site = useMemo(
    () => status?.sites.find((s) => s.site_key === siteKey) ?? null,
    [status, siteKey],
  );

  // Formular bei Portalwechsel (und erstem Laden) aus der gespeicherten Konfiguration fuellen —
  // nicht bei jedem Status-Neuladen, sonst verwirft ein Neuladen ungespeicherte Eingaben.
  useEffect(() => {
    if (site && formSiteRef.current !== site.site_key) {
      formSiteRef.current = site.site_key;
      setForm(site.config);
      setPreview(null);
      setMeldung(null);
    }
  }, [site]);

  // Vorschau der UNGESPEICHERTEN Einstellung, entprellt.
  useEffect(() => {
    if (!form) return undefined;
    let abgebrochen = false;
    const t = setTimeout(async () => {
      const r = await fetchData<CloudPreview>(`${API}/${siteKey}/preview`, {
        method: 'POST',
        body: JSON.stringify({ config: form }),
      });
      if (!abgebrochen && r.success && r.data) setPreview(r.data);
    }, 500);
    return () => {
      abgebrochen = true;
      clearTimeout(t);
    };
  }, [form, siteKey]);

  const setStyle = (key: keyof CloudConfig['style'], value: string | number) =>
    setForm((f) => (f ? { ...f, style: { ...f.style, [key]: value } } : f));

  const entries: CloudEntry[] = preview?.entries ?? site?.entries ?? [];
  const excluded: CloudExcluded[] = (preview?.excluded ?? site?.excluded ?? []).filter(
    (x) => x.reason !== 'no_profile',
  );

  const verschiebe = (idx: number, dir: -1 | 1) => {
    const ids = entries.map((e) => e.brokerId);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setForm((f) => (f ? { ...f, order: ids } : f));
  };
  const verstecke = (id: number) =>
    setForm((f) => (f ? { ...f, hidden: Array.from(new Set([...f.hidden, id])) } : f));
  const zeige = (id: number) =>
    setForm((f) => (f ? { ...f, hidden: f.hidden.filter((h) => h !== id) } : f));

  const speichern = async () => {
    if (!form) return;
    // Antwort gehoert zu DIESEM Portal: wurde inzwischen gewechselt, darf sie das Formular des
    // anderen Portals nicht ueberschreiben (sonst veroeffentlicht der naechste Klick A's Liste auf B).
    const portal = siteKey;
    setBusy(true);
    setMeldung(null);
    const r = await fetchData<{ config: CloudConfig; publish: { status: string; error?: string } }>(
      `${API}/${portal}/config`,
      { method: 'PUT', body: JSON.stringify(form) },
    );
    setBusy(false);
    if (formSiteRef.current !== portal) {
      ladeStatus();
      return;
    }
    if (r.success && r.data) {
      setForm(r.data.config);
      const p = r.data.publish;
      setMeldung(
        p.status === 'failed'
          ? { ok: false, text: `Gespeichert, aber Veröffentlichung gescheitert: ${p.error}` }
          : {
              ok: true,
              text:
                p.status === 'published'
                  ? 'Gespeichert und veröffentlicht.'
                  : 'Gespeichert (Leiste unverändert).',
            },
      );
      ladeStatus();
    } else {
      setMeldung({ ok: false, text: r.error ?? 'Speichern fehlgeschlagen' });
    }
  };

  const veroeffentlichen = async () => {
    const portal = siteKey;
    setBusy(true);
    setMeldung(null);
    const r = await fetchData<{ status: string; error?: string }>(`${API}/${portal}/publish`, {
      method: 'POST',
      body: JSON.stringify({ force: true }),
    });
    setBusy(false);
    if (formSiteRef.current !== portal) {
      ladeStatus();
      return;
    }
    setMeldung(
      r.success
        ? { ok: true, text: 'Veröffentlicht.' }
        : { ok: false, text: r.error ?? 'Veröffentlichen fehlgeschlagen' },
    );
    ladeStatus();
  };

  const ungespeichert = !!site && !!form && JSON.stringify(form) !== JSON.stringify(site.config);

  if (laden && !status) return <CircularProgress />;
  if (fehler && !status) return <Alert severity="error">{fehler}</Alert>;
  if (!status || !site || !form) return null;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Die Leiste zeigt automatisch jeden Broker, dessen Profil auf dem Portal ausgespielt ist und
        ein Katalog-Logo hat. Hier lassen sich Broker ausblenden, sortieren und das Aussehen
        anpassen. Änderungen erreichen die Portale nach höchstens rund 20 Minuten (Abgleich alle 10
        min plus Cache).
        {!status.publishes_to_prod && (
          <>
            {' '}
            <b>Diese Umgebung schreibt nach cloud-dev/ — die Portale sehen davon nichts.</b>
          </>
        )}
      </Alert>

      <Tabs
        value={siteKey}
        onChange={(_e, v) => {
          if (!busy) setSiteKey(v);
        }}
        sx={{ mb: 2 }}
        variant="scrollable"
      >
        {status.sites.map((s) => (
          <Tab
            key={s.site_key}
            value={s.site_key}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {s.name}
                <StatusChip s={s} />
              </Box>
            }
          />
        ))}
      </Tabs>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', lg: '420px 1fr' }} gap={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            In der Leiste ({form.enabled ? entries.length : 0})
          </Typography>
          {!form.enabled && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Leiste ist ausgeschaltet — das Portal zeigt nichts.
            </Alert>
          )}
          <Table size="small">
            <TableBody>
              {entries.map((e, i) => (
                <TableRow key={e.brokerId} hover>
                  <TableCell sx={{ width: 64 }}>
                    <Box
                      component="img"
                      src={e.logoUrl}
                      alt={e.name}
                      sx={{ maxHeight: 24, maxWidth: 60, objectFit: 'contain' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={e.href}>
                      <span>{e.name}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton
                      size="small"
                      disabled={i === 0}
                      onClick={() => verschiebe(i, -1)}
                      aria-label="nach oben"
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={i === entries.length - 1}
                      onClick={() => verschiebe(i, 1)}
                      aria-label="nach unten"
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <Tooltip title="Ausblenden">
                      <IconButton
                        size="small"
                        onClick={() => verstecke(e.brokerId)}
                        aria-label="ausblenden"
                      >
                        <VisibilityOffOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!entries.length && (
                <TableRow>
                  <TableCell colSpan={3}>
                    Kein Broker — die Leiste bleibt auf dem Portal unsichtbar.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {excluded.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Nicht in der Leiste
              </Typography>
              <Table size="small">
                <TableBody>
                  {excluded.map((x) => (
                    <TableRow key={x.brokerId}>
                      <TableCell>{x.name}</TableCell>
                      <TableCell>{EXCLUSION_LABEL[x.reason] ?? x.reason}</TableCell>
                      <TableCell align="right">
                        {x.reason === 'hidden' && (
                          <Tooltip title="Wieder zeigen">
                            <IconButton
                              size="small"
                              onClick={() => zeige(x.brokerId)}
                              aria-label="wieder zeigen"
                            >
                              <VisibilityOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
              }
              label="Leiste auf diesem Portal zeigen"
            />
            <TextField
              size="small"
              label="Vergleichs-Link (leer = ohne Link)"
              value={form.compareHref ?? ''}
              helperText={`Vorgabe: ${site.default_compare_href ?? '—'}`}
              onChange={(e) =>
                setForm({
                  ...form,
                  compareHref: e.target.value.trim() ? e.target.value.trim() : null,
                })
              }
            />
            <Box display="flex" gap={1}>
              <TextField
                size="small"
                fullWidth
                label="Text Vergleichs-Link"
                value={form.compareLabel}
                onChange={(e) => setForm({ ...form, compareLabel: e.target.value })}
              />
              <TextField
                size="small"
                sx={{ width: 140 }}
                label="Hinweis"
                value={form.disclosureText}
                onChange={(e) => setForm({ ...form, disclosureText: e.target.value })}
              />
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
              {FARB_FELDER.map((f) => (
                <TextField
                  key={f.key}
                  size="small"
                  label={f.label}
                  value={form.style[f.key]}
                  onChange={(e) => setStyle(f.key, e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          mr: 1,
                          borderRadius: '3px',
                          border: '1px solid #ccc',
                          background: String(form.style[f.key]),
                        }}
                      />
                    ),
                  }}
                />
              ))}
              {ZAHL_FELDER.map((f) => (
                <TextField
                  key={f.key}
                  size="small"
                  type="number"
                  label={f.label}
                  inputProps={{ min: f.min, max: f.max }}
                  value={form.style[f.key]}
                  onChange={(e) => setStyle(f.key, Number(e.target.value))}
                />
              ))}
              <TextField
                select
                size="small"
                label="Ausrichtung"
                value={form.style.align}
                onChange={(e) => setStyle('align', e.target.value)}
              >
                <MenuItem value="start">links</MenuItem>
                <MenuItem value="center">zentriert</MenuItem>
              </TextField>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Farben als #rrggbb, rgb(…) oder transparent. Ungültige Werte setzt der Server beim
              Speichern auf die Vorgabe zurück.
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="contained"
                disabled={busy}
                onClick={speichern}
                startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadOutlined />}
              >
                Speichern &amp; veröffentlichen
              </Button>
              <Button
                variant="outlined"
                disabled={busy || ungespeichert}
                onClick={veroeffentlichen}
              >
                Jetzt erneut veröffentlichen
              </Button>
              {ungespeichert && <Chip size="small" color="warning" label="ungespeichert" />}
            </Box>
            {meldung && <Alert severity={meldung.ok ? 'success' : 'error'}>{meldung.text}</Alert>}
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6">Vorschau</Typography>
            <Typography variant="caption" color="text.secondary">
              (ungespeicherter Stand, zählt nicht)
            </Typography>
            <Box flexGrow={1} />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mobil ? 'm' : 'd'}
              onChange={(_e, v) => v && setMobil(v === 'm')}
            >
              <ToggleButton value="d">Desktop</ToggleButton>
              <ToggleButton value="m">Mobil</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* Desktop = 1180 px Rahmen (Leiste max. 1150 + Rand), sonst zeigte die schmale Spalte das
              Tablet-Raster (3 je Reihe ab < 1150 px). Breiter als die Spalte → waagrecht scrollen. */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#fff',
              overflowX: 'auto',
            }}
          >
            {preview ? (
              <iframe
                ref={iframeRef}
                title={`Vorschau Broker-Cloud ${siteKey}`}
                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                srcDoc={vorschauHtml(siteKey, preview.code)}
                style={{
                  width: mobil ? 390 : 1180,
                  height: iframeHoehe,
                  border: 0,
                  display: 'block',
                  margin: mobil ? '0 auto' : 0,
                }}
              />
            ) : (
              <Box p={2}>
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Stand
          </Typography>
          <Typography variant="body2">
            Veröffentlicht:{' '}
            {site.published
              ? `${site.published.published_at} (${site.published.broker_ids.length} Broker)`
              : '—'}
          </Typography>
          {site.file_url && (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              Datei:{' '}
              <a href={site.file_url} target="_blank" rel="noopener noreferrer">
                {site.file_url}
              </a>
            </Typography>
          )}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Einbindung im Portal-Footer (einmalig, durch die Entwicklung)
          </Typography>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Box
              component="pre"
              sx={{
                flex: 1,
                m: 0,
                p: 1,
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {site.embed}
            </Box>
            <Tooltip title="Kopieren">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard?.writeText(site.embed)}
                aria-label="Einbindung kopieren"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      </Box>

      <CloudReport sites={status.sites} />
    </Box>
  );
};

function CloudReport({ sites }: { sites: CloudSiteStatus[] }) {
  const [from, setFrom] = useState(() => heuteBerlin(29));
  const [to, setTo] = useState(() => heuteBerlin(0));
  const [group, setGroup] = useState<'day' | 'month'>('month');
  const [site, setSite] = useState<string>('');
  const [rows, setRows] = useState<CloudReportRow[] | null>(null);
  const [stand, setStand] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    const params = new URLSearchParams({ from, to, group, ...(site ? { site } : {}) });
    fetchData<{ rows: CloudReportRow[]; rolled_up_through: string | null }>(
      `${API}/report?${params}`,
    ).then((r) => {
      if (abgebrochen) return;
      if (r.success && r.data) {
        setRows(r.data.rows);
        setStand(r.data.rolled_up_through);
        setFehler(null);
      } else {
        setFehler(r.error ?? 'Laden fehlgeschlagen');
      }
    });
    return () => {
      abgebrochen = true;
    };
  }, [from, to, group, site]);

  const summe = useMemo(
    () =>
      (rows ?? []).reduce(
        (a, r) => ({
          vc: a.vc + r.views_clean,
          vf: a.vf + r.views_flagged,
          cc: a.cc + r.clicks_clean,
          cf: a.cf + r.clicks_flagged,
        }),
        { vc: 0, vf: 0, cc: 0, cf: 0 },
      ),
    [rows],
  );
  const csvUrl = buildDownloadUrl(`${API}/report.csv`, {
    from,
    to,
    group,
    ...(site ? { site } : {}),
  });
  const ctr = (c: number, v: number) =>
    v > 0 ? `${((100 * c) / v).toFixed(2).replace('.', ',')} %` : '—';

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <Typography variant="h6">Auswertung</Typography>
        <Box flexGrow={1} />
        <TextField
          size="small"
          type="date"
          label="von"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label="bis"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          size="small"
          label="Portal"
          value={site}
          onChange={(e) => setSite(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">alle</MenuItem>
          {sites.map((s) => (
            <MenuItem key={s.site_key} value={s.site_key}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={group}
          onChange={(_e, v) => v && setGroup(v)}
        >
          <ToggleButton value="day">Tag</ToggleButton>
          <ToggleButton value="month">Monat</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" variant="outlined" startIcon={<DownloadOutlined />} href={csvUrl}>
          CSV
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        Sichtbar = Logo mindestens 1 s zu mindestens 50 % im Bild (je Seitenaufruf und Broker
        einmal). Gefiltert = Bots, fremde Herkunft, Häufung, Doppelklicks, Tests — nicht in den
        Hauptzahlen. Tageswerte vollständig bis {stand ?? '—'}, heute stündlich.
      </Typography>
      {fehler && <Alert severity="error">{fehler}</Alert>}
      {rows && (
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{group === 'month' ? 'Monat' : 'Tag'}</TableCell>
                <TableCell>Portal</TableCell>
                <TableCell>Broker</TableCell>
                <TableCell align="right">Sichtbar</TableCell>
                <TableCell align="right">gefiltert</TableCell>
                <TableCell align="right">Klicks</TableCell>
                <TableCell align="right">gefiltert</TableCell>
                <TableCell align="right">Klickrate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.period}|${r.site_key}|${r.broker_id}`} hover>
                  <TableCell>{r.period}</TableCell>
                  <TableCell>{r.site_key}</TableCell>
                  <TableCell>{r.broker_name}</TableCell>
                  <TableCell align="right">{r.views_clean.toLocaleString('de-DE')}</TableCell>
                  <TableCell align="right">{r.views_flagged.toLocaleString('de-DE')}</TableCell>
                  <TableCell align="right">{r.clicks_clean.toLocaleString('de-DE')}</TableCell>
                  <TableCell align="right">{r.clicks_flagged.toLocaleString('de-DE')}</TableCell>
                  <TableCell align="right">{ctr(r.clicks_clean, r.views_clean)}</TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={8}>Keine Daten im Zeitraum.</TableCell>
                </TableRow>
              )}
              {rows.length > 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <b>Summe</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>{summe.vc.toLocaleString('de-DE')}</b>
                  </TableCell>
                  <TableCell align="right">{summe.vf.toLocaleString('de-DE')}</TableCell>
                  <TableCell align="right">
                    <b>{summe.cc.toLocaleString('de-DE')}</b>
                  </TableCell>
                  <TableCell align="right">{summe.cf.toLocaleString('de-DE')}</TableCell>
                  <TableCell align="right">{ctr(summe.cc, summe.vc)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

export default BrokerCloudTab;
